import React, { useEffect, useState } from "react";
import axiosClient from "../api/axiosClient";

const initialForm = { platform: "", url: "", iconClass: "", sortOrder: "" };

const SocialLinks = () => {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [form, setForm] = useState(initialForm);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    
    useEffect(() => {
        fetchLinks();
        // eslint-disable-next-line
    }, []);
    
    const fetchLinks = async () => {
        setLoading(true);
        setError("");
        try {
            const response = await axiosClient.get("/profile/social");
            setLinks(response.data.data || []);
        } catch (err) {
            setError("Failed to load social links.");
        } finally {
            setLoading(false);
        }
    };
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
    };
    
    const handleAdd = async (e) => {
        e.preventDefault();
        if (!form.platform || !form.url || !form.iconClass || !form.sortOrder) {
            alert("Please fill in all fields.");
            return;
        }
        setSaving(true);
        try {
            const response = await axiosClient.post("/profile/social", {
                ...form,
                sortOrder: Number(form.sortOrder),
            });
            setLinks([...links, response.data.data]);
            setForm(initialForm);
        } catch (err) {
            alert("Failed to add social link.");
        } finally {
            setSaving(false);
        }
    };
    
    const startEdit = (link) => {
        setEditingId(link.id);
        setForm({
            platform: link.platform || "",
            url: link.url || "",
            iconClass: link.iconClass || "",
            sortOrder: link.sortOrder?.toString() || "",
        });
    };
    
    const cancelEdit = () => {
        setEditingId(null);
        setForm(initialForm);
    };
    
    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!form.platform || !form.url || !form.iconClass || !form.sortOrder) {
            alert("Please fill in all fields.");
            return;
        }
        setSaving(true);
        try {
            const response = await axiosClient.put(`/profile/social/${editingId}`, {
                ...form,
                sortOrder: Number(form.sortOrder),
            });
            setLinks(links.map((link) => (link.id === editingId ? response.data.data : link)));
            cancelEdit();
        } catch (err) {
            alert("Failed to update social link.");
        } finally {
            setSaving(false);
        }
    };
    
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this social link?")) return;
        try {
            await axiosClient.delete(`/profile/social/${id}`);
            setLinks(links.filter((link) => link.id !== id));
        } catch (err) {
            alert("Failed to delete social link.");
        }
    };
    
    if (loading) return <p>Loading social links...</p>;
    if (error) return <p className="text-danger">{error}</p>;
    
    return (
        <div className="container mt-4" style={{ maxWidth: "900px" }}>
            <h2 className="mb-4">Manage Social Links</h2>
            
            {/* Card with Add/Edit Form */}
            <div className="card mb-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">{editingId ? "Edit Social Link" : "Add New Social Link"}</h5>
                    <form className="row g-3" onSubmit={editingId ? handleUpdate : handleAdd}>
                        <div className="col-md-3">
                            <input
                                type="text"
                                name="platform"
                                className="form-control"
                                placeholder="Platform"
                                value={form.platform}
                                onChange={handleChange}
                                disabled={saving}
                                required
                            />
                        </div>
                        <div className="col-md-3">
                            <input
                                type="url"
                                name="url"
                                className="form-control"
                                placeholder="URL"
                                value={form.url}
                                onChange={handleChange}
                                disabled={saving}
                                required
                            />
                        </div>
                        <div className="col-md-3">
                            <input
                                type="text"
                                name="iconClass"
                                className="form-control"
                                placeholder="Icon Class (e.g., fab fa-linkedin)"
                                value={form.iconClass}
                                onChange={handleChange}
                                disabled={saving}
                                required
                            />
                        </div>
                        <div className="col-md-2">
                            <input
                                type="number"
                                name="sortOrder"
                                className="form-control"
                                placeholder="Sort Order"
                                value={form.sortOrder}
                                onChange={handleChange}
                                disabled={saving}
                                required
                            />
                        </div>
                        <div className="col-md-1 d-flex align-items-center">
                            {editingId ? (
                                <>
                                    <button type="submit" className="btn btn-success btn-sm me-2" disabled={saving}>
                                        Save
                                    </button>
                                    <button type="button" className="btn btn-secondary btn-sm" onClick={cancelEdit} disabled={saving}>
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                                    Add
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
            
            {/* Table View */}
            <div className="table-responsive">
                <table className="table table-striped align-middle">
                    <thead>
                    <tr>
                        <th>Platform</th>
                        <th>URL</th>
                        <th>Icon</th>
                        <th>Sort Order</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {links.length === 0 ? (
                        <tr>
                            <td colSpan={5} className="text-center">
                                No social links added yet.
                            </td>
                        </tr>
                    ) : (
                        links
                            .slice()
                            .sort((a, b) => a.sortOrder - b.sortOrder)
                            .map((link) => (
                                <tr key={link.id}>
                                    <td>{link.platform}</td>
                                    <td>
                                        <a href={link.url} target="_blank" rel="noopener noreferrer">
                                            {link.url}
                                        </a>
                                    </td>
                                    <td>
                                        <i className={link.iconClass}></i> <span className="ms-1">{link.iconClass}</span>
                                    </td>
                                    <td>{link.sortOrder}</td>
                                    <td>
                                        <button
                                            className="btn btn-sm btn-outline-primary me-2"
                                            onClick={() => startEdit(link)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => handleDelete(link.id)}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SocialLinks;
